import { formatUsdCompact, formatUsdFull } from "@po/shared";
import type { Spend } from "../lib/api";
import { useDashboard } from "../pages/Dashboard";
import { MONEY_CAPTION } from "../lib/format";
import { StageBadge } from "../components/badges";
import { CATEGORICAL, ChartCard, Donut, HBars, OTHER_COLOR } from "../components/charts";
import { useTabQuery } from "./common";

export function SpendTab() {
  const { filters, toggle, open } = useDashboard();
  const { data: s } = useTabQuery<Spend>("/spend");
  if (!s) return <div className="card h-64 animate-pulse" />;
  // Colour follows the entity: purchase types keep their slot in a stable (alphabetical) order.
  const typeOrder = s.byPurchaseType.map((d) => d.name).filter((n) => n !== "Other").sort();
  const typeColor = (n: string) => (n === "Other" ? OTHER_COLOR : (CATEGORICAL[typeOrder.indexOf(n)] ?? OTHER_COLOR));
  const sel = (key: "vessel" | "supplier" | "purchaseType" | "account") => (k: string) => k !== "Other" && toggle(key, k);
  const money = { head: ["", "USD"] };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {(
          [
            ["Committed", s.totals.committed, "POs placed in the date range"],
            ["PO pending receipt", s.totals.openCommitments, "Ordered, not yet received"],
          ] as const
        ).map(([t, v, sub]) => (
          <div key={t} className="card p-3">
            <div className="label">{t}</div>
            <div className="text-[26px] font-extrabold tnum">{formatUsdCompact(v)}</div>
            <div className="text-[12px] text-muted">{sub}</div>
            <div className="text-[10.5px] text-muted">{MONEY_CAPTION}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="By purchase type" caption={`Committed, split by line · ${MONEY_CAPTION}`} table={{ ...money, rows: s.byPurchaseType.map((d) => [d.name, formatUsdFull(d.usd)]) }}>
          <Donut
            data={s.byPurchaseType.map((d) => ({ key: d.name, label: d.name, value: d.usd, color: typeColor(d.name) }))}
            selected={filters.purchaseType ?? []}
            onSelect={sel("purchaseType")}
            format={formatUsdCompact}
          />
        </ChartCard>
        <ChartCard title="By technical account" caption={`Committed, split by line · ${MONEY_CAPTION}`} table={{ ...money, rows: s.byAccount.map((d) => [d.name, formatUsdFull(d.usd)]) }}>
          <HBars
            data={s.byAccount.map((d) => ({ key: d.name, label: d.name, value: d.usd, color: d.name === "Other" ? OTHER_COLOR : "var(--stage-ordered)" }))}
            selected={filters.account ?? []}
            onSelect={sel("account")}
            format={formatUsdCompact}
          />
        </ChartCard>
        <ChartCard title="By vessel" caption={`Committed, top 8 · ${MONEY_CAPTION}`} table={{ ...money, rows: s.byVessel.map((d) => [d.name, formatUsdFull(d.usd)]) }}>
          <HBars
            data={s.byVessel.map((d) => ({ key: d.name, label: d.name, value: d.usd, color: d.name === "Other" ? OTHER_COLOR : "var(--stage-ordered)" }))}
            selected={filters.vessel ?? []}
            onSelect={sel("vessel")}
            format={formatUsdCompact}
          />
        </ChartCard>
        <ChartCard title="By supplier" caption={`Committed, top 8 · ${MONEY_CAPTION}`} table={{ ...money, rows: s.bySupplier.map((d) => [d.name, formatUsdFull(d.usd)]) }}>
          <HBars
            data={s.bySupplier.map((d) => ({ key: d.name, label: d.name, value: d.usd, color: d.name === "Other" ? OTHER_COLOR : "var(--stage-ordered)" }))}
            selected={filters.supplier ?? []}
            onSelect={sel("supplier")}
            format={formatUsdCompact}
          />
        </ChartCard>
      </div>
      <section className="card p-3 md:p-4" aria-label="Largest POs pending receipt">
        <h3 className="text-[14px] font-bold">Largest POs pending receipt</h3>
        <p className="text-[11px] text-muted mb-2">Explains the PO pending receipt total · {MONEY_CAPTION}</p>
        <ul className="divide-y divide-line">
          {s.largestOpen.map((p) => (
            <li key={p.no}>
              <button className="w-full flex items-center gap-2 py-1.5 text-left hover:bg-hover rounded px-1 text-[13px]" onClick={() => open({ type: "po", no: p.no })}>
                <span className="font-semibold text-brand">{p.no}</span>
                <StageBadge stage={p.stage} lens="po" />
                <span className="flex-1 truncate text-muted">
                  {p.vessel} · {p.supplier}
                </span>
                <span className="tnum font-semibold">{formatUsdFull(p.openCommitmentUsd)}</span>
              </button>
            </li>
          ))}
          {!s.largestOpen.length && <li className="text-muted text-[13px] py-2">No POs pending receipt.</li>}
        </ul>
      </section>
    </div>
  );
}
